// Customer-360 原生態系 (base ecosystem) editor — restful#84 (owner追加 2026-07-12).
// A user normally inherits users.base_ecosystem_id from its registration client's
// client_apps.ecosystem_id; this panel lets an operator CORRECT a mis-attributed
// account (e.g. a consumer who should be Living Style but landed on the platform).
// Operator-gated + audited server-side (reason + case_id required). Mirrors the
// eco-* attribute model shown read-only in the profile card and the /users browse.

import { useEffect, useState } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { actionErrorMessage } from '../_shared/actionError';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import { listEcosystems } from '../ecosystem/ecosystemApi';
import type { Ecosystem } from '../ecosystem/ecosystemApi';
import { setUserEcosystem } from './usersApi';
import type { AdminUser } from './usersApi';

// eco-kinio-ai 的 row display_name 是小寫 "kinio.ai";顯示端統一成品牌字 "Kinio.AI"。
function ecoLabel(e: Ecosystem): string {
  return e.ecosystem_id === 'eco-kinio-ai' ? 'Kinio.AI' : e.display_name || e.ecosystem_id;
}

const CLEAR = '__clear__'; // sentinel select value → clear attribution (empty ecosystem_id)

export function UserEcosystemPanel({ user, onChanged }: { user: AdminUser; onChanged: () => void }) {
  const { role } = useAuth();
  const writable = canAdminWrite(role);
  const currentId = user.base_ecosystem_id ?? '';
  const currentName = user.base_ecosystem_name || user.base_ecosystem_id || '（未歸屬）';

  const [ecosystems, setEcosystems] = useState<Ecosystem[]>([]);
  const [target, setTarget] = useState<string>(currentId);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [caseId, setCaseId] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [result, setResult] = useState<{ ok: boolean; message: string; requestId?: string | null } | null>(null);

  useEffect(() => {
    setTarget(currentId);
  }, [currentId]);

  useEffect(() => {
    let alive = true;
    // active-only inventory (a suspended ecosystem is not a valid target — backend 422s).
    listEcosystems({ status: 'active', limit: 200 })
      .then((r) => alive && setEcosystems(r))
      .catch(() => {
        /* best-effort; the dropdown just stays sparse */
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!writable) {
    return (
      <section className="card adm-actionbar">
        <div className="adm-actionbar-head">
          <h2 className="adm-card-title">基礎生態系歸屬</h2>
          <span className="pill pill-neutral">唯讀</span>
        </div>
        <p className="text-secondary" style={{ margin: 0 }}>
          目前角色僅能檢視生態系歸屬。原生態系：{currentName}
        </p>
      </section>
    );
  }

  const selectedValue = target === '' ? CLEAR : target;
  const targetEcosystemId = target; // '' = clear
  const changed = targetEcosystemId !== currentId;
  const targetName =
    targetEcosystemId === ''
      ? '（清除歸屬）'
      : ecosystems.find((e) => e.ecosystem_id === targetEcosystemId)
        ? ecoLabel(ecosystems.find((e) => e.ecosystem_id === targetEcosystemId) as Ecosystem)
        : targetEcosystemId;

  function openConfirm() {
    setReason('');
    setCaseId('');
    setDialogError(null);
    setResult(null);
    setConfirming(true);
  }

  async function apply() {
    setBusy(true);
    setDialogError(null);
    try {
      const { requestId } = await setUserEcosystem(user.user_id, targetEcosystemId, {
        reason: reason.trim() || undefined,
        caseId: caseId.trim() || undefined,
      });
      setResult({ ok: true, message: `原生態系已更新為「${targetName}」。`, requestId });
      setConfirming(false);
      onChanged();
    } catch (e) {
      setDialogError(actionErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card adm-actionbar">
      <div className="adm-actionbar-head">
        <h2 className="adm-card-title">基礎生態系歸屬</h2>
        <span className="pill pill-info">變更會稽核留痕</span>
      </div>
      <p className="text-secondary adm-inline-note">
        設定或更正此帳號的**註冊(原)生態系**（`base_ecosystem_id`）。一般由註冊 client 繼承;此處供營運端改正誤歸屬。目前：
        <strong>{currentName}</strong>
      </p>
      <div className="adm-phone-row">
        <label className="adm-field" style={{ flex: 1 }}>
          <span className="text-secondary adm-field-label">目標生態系</span>
          <select
            value={selectedValue}
            onChange={(e) => setTarget(e.target.value === CLEAR ? '' : e.target.value)}
            disabled={busy}
          >
            {ecosystems.map((e) => (
              <option key={e.ecosystem_id} value={e.ecosystem_id}>
                {ecoLabel(e)}
              </option>
            ))}
            {/* keep the current id selectable even if it's not in the active list */}
            {currentId && !ecosystems.some((e) => e.ecosystem_id === currentId) && (
              <option value={currentId}>{currentName}（目前）</option>
            )}
            <option value={CLEAR}>（清除歸屬）</option>
          </select>
        </label>
        <button className="btn-primary btn-sm" disabled={busy || !changed} onClick={openConfirm}>
          變更基礎生態系
        </button>
      </div>

      {result && (
        <div className={`adm-action-result ${result.ok ? 'is-ok' : 'is-err'}`} role="status">
          <p style={{ margin: 0 }}>{result.message}</p>
          {result.requestId && (
            <p className="adm-mono adm-result-meta" style={{ margin: 0, opacity: 0.85 }}>
              request_id: {result.requestId}
            </p>
          )}
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title="變更基礎生態系"
          confirmLabel="確認變更"
          tone="warn"
          busy={busy}
          // 後端要求 reason + case_id;缺任一即拒(400),故 gate 確認鈕
          confirmDisabled={reason.trim() === '' || caseId.trim() === ''}
          error={dialogError}
          onConfirm={() => void apply()}
          onCancel={() => {
            if (!busy) setConfirming(false);
          }}
          body={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-6)' }}>
              <p style={{ margin: 0 }}>
                將此帳號的原生態系由「{currentName}」變更為「{targetName}」。這會改變其跨生態系的裝置存取範圍;新註冊繼承不受影響。此操作會被稽核記錄。
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                <label className="adm-field">
                  <span className="text-secondary adm-field-label">原因（必填）</span>
                  <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy} placeholder="例：更正誤歸屬" style={{ width: '100%' }} />
                </label>
                <label className="adm-field">
                  <span className="text-secondary adm-field-label">案件編號（必填）</span>
                  <input value={caseId} onChange={(e) => setCaseId(e.target.value)} disabled={busy} placeholder="例：CS-84" style={{ width: '100%' }} />
                </label>
              </div>
            </div>
          }
        />
      )}
    </section>
  );
}
